/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { node, string, shape, func, number, bool } from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'clsx';
import uuid from 'uuid/v1';

import {
  ADD_PICTURE_SUCCESS,
  addPicture as addPictureAction,
} from 'actions/user';

import {
  UPLOAD_IMAGE_SUCCESS,
  UPLOAD_IMAGE_FAILURE,
  uploadImage as uploadImageAction,
} from 'actions/cloudinary';

import FileDropZone from './FileDropZone/FileDropZone';

import styles from './file-input.scss';

/**
 * This is the FileInput component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class FileInput extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {Object}
   */
  static propTypes = {
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    user: shape({
      type: string.isRequired,
    }).isRequired,
    uploadImage: func.isRequired,
    addPicture: func.isRequired,
    onChange: func,
    onError: func,
    onComplete: func,
    className: string,
    id: string.isRequired,
    index: number.isRequired,
    children: node,
    sending: bool,
    multiple: bool,
  };

  /**
   * Declare default props.
   *
   * @type {Object}
   */
  static defaultProps = {
    className: '',
    children: null,
    sending: false,
    multiple: false,
    onChange: () => {},
    onError: () => {},
    onComplete: () => {},
  };

  state = {
    isFileHover: false,
  };

  selectFile = event => {
    event.stopPropagation();
    event.preventDefault();

    const { files } = event.target;

    this.prepareImageUpload(files);
  };

  onDrop = files => this.prepareImageUpload(files);

  onHover = isFileHover => this.setState({ isFileHover });

  prepareImageUpload = async files => {
    const { index, multiple, onChange } = this.props;
    const newIndex = parseInt(index, 10);

    onChange(); // trigger onChange for parent comp.

    if (multiple) {
      for (let index = 0; index < files.length; index++) {
        const file = files[index];

        await this.uploadImage(newIndex + index, file);
      }
    } else {
      await this.uploadImage(newIndex, files[0]);
    }
  };

  uploadImage = (index, file) => {
    const {
      auth: { token },
      user,
      uploadImage,
      addPicture,
      onError,
      onComplete,
    } = this.props;

    if (!file) {
      return;
    }

    if (['image/jpeg', 'image/gif', 'image/png'].indexOf(file.type) === -1) {
      onError({ error: 'Not a valid image' });
      return;
    }

    const key = uuid() + /(?:\.([^.]+))?$/.exec(file.name)[0];

    const context = [`user_id=${user.user_id}`];

    return new Promise(resolve => {
      uploadImage({
        key,
        file,
        context,
        contentType: file.type,
      }).then(({ type, payload }) => {
        if (type === UPLOAD_IMAGE_SUCCESS) {
          addPicture(token, {
            role_type: user.type,
            indexes: [index],
            cloudinary_public_ids: [payload.key],
          }).then(addPictureAction => {
            if (addPictureAction.type === ADD_PICTURE_SUCCESS) {
              onComplete({
                title: file.name,
                preview: payload.url,
              });

              resolve(true);
            }
          });
        }

        // @NOTE: Handle error the right way.
        if (type === UPLOAD_IMAGE_FAILURE) {
          onError({ error: 'UPLOAD_IMAGE_FAILURE' });
        }
      });
    });
  };

  /**
   * Render FileInput.
   *
   * @return {Object}
   */
  render() {
    const { isFileHover } = this.state;
    const { className, id, children, multiple, sending } = this.props;

    return (
      <FileDropZone onHover={this.onHover} onDrop={this.onDrop}>
        <label
          htmlFor={id}
          className={classNames(className, {
            [styles.sending]: sending,
            [styles.active]: isFileHover,
          })}
        >
          {children && children}
          <input
            id={id}
            type="file"
            className={styles.input}
            onChange={this.selectFile}
            accept="image/gif, image/jpeg, image/png"
            multiple={multiple}
          />
        </label>
      </FileDropZone>
    );
  }
}

export default connect(
  ({ auth, user }) => ({ auth, user }),
  {
    uploadImage: uploadImageAction,
    addPicture: addPictureAction,
  }
)(FileInput);
